import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/api/client";
import { ProfileAvatar } from "@/components/build/ProfileAvatar";
import { toast } from "sonner";
import { Mail, X } from "lucide-react";

/**
 * Send a Note — Phase 4 UI (2026-08). Built to the approved "NJM Build - Send a Note
 * (standalone)" mockup: Montserrat · black/charcoal (ink) · NJM yellow (brand #FFC817) · thin
 * hairline borders · no chat bubbles/gradients/presence. One shared modal + thread drawer +
 * header ✉ MESSAGES drawer, wired to the Phase 1-3 backend (/build/notes/*). V1 ships REPORT
 * only (no BLOCK). All payloads are already public-safe; the UI is never the access boundary.
 */

const NOTE_MAX = 2000;
const NotesCtx = createContext(null);
export const useNotes = () => useContext(NotesCtx) || {};

function initialsOf(name) {
  return (name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}
export function firstName(name) {
  const first = (name || "").trim().split(/\s+/)[0] || "";
  return first.length <= 2 ? (name || "").trim() : first; // "A S Aravind" -> full name, not "A"
}
function subtitleOf(m) {
  // Notes fix (Creative Radar Wave 3, 2026-11): a staff sender has no primary_role/city at all
  // — the backend stamps a fixed `tag: "NJM"` for them instead (never a role word like
  // "NJM Finance", per the founder's spec) and this is the ONE place that tag is ever shown.
  if (m?.tag) return m.tag;
  const city = m?.canonical_location && m.canonical_location.trim().toLowerCase() !== "unknown" ? m.canonical_location : null;
  return [m?.primary_role, city].filter(Boolean).join(" · ");
}
function relTime(iso) {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return `${Math.floor(d / 7)}w`;
}
function stampOf(m, name) {
  const d = m.created_at ? new Date(m.created_at) : null;
  const date = d ? d.toLocaleDateString(undefined, { day: "2-digit", month: "short" }).toUpperCase() : "";
  const time = d ? d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "";
  return `${(name || "").toUpperCase()} · ${date} · ${time}`;
}

function Avatar({ member, size = 40, dark }) {
  return (
    <div className={`rounded-full flex items-center justify-center font-bold shrink-0 overflow-hidden ${dark ? "bg-ink text-brand" : "bg-ink text-brand"}`}
      style={{ width: size, height: size, fontSize: size * 0.3 }}>
      <ProfileAvatar freelancerId={member?.id} hasPhoto={Boolean(member?.profile_image)} alt="" className="h-full w-full object-cover" fallback={initialsOf(member?.name)} />
    </div>
  );
}

export function NotesProvider({ children }) {
  const [sendTarget, setSendTarget] = useState(null); // { member, context, onDone }
  const [threadId, setThreadId] = useState(null);
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [readTick, setReadTick] = useState(0); // bumps when a thread is read — lets the
  // notifications feed (owned by HomeTab/NotificationsPage) refetch in lockstep, so a note
  // notification's unread dot/badge clears when its conversation is read. Decoupled by design:
  // the provider is a parent of those feeds and must not reach into their local state.
  const enabled = useRef(true);

  const refreshUnread = useCallback(async () => {
    try {
      const r = await api.get("/v1/build/notes/conversations");
      setUnreadCount(r.data?.unread_count || 0);
    } catch { enabled.current = false; }
  }, []);

  useEffect(() => {
    refreshUnread();
    const t = setInterval(() => { if (enabled.current) refreshUnread(); }, 45000);
    return () => clearInterval(t);
  }, [refreshUnread]);

  const afterThreadActivity = useCallback(() => { refreshUnread(); setReadTick((t) => t + 1); }, [refreshUnread]);

  const value = {
    unreadCount,
    readTick,
    refreshUnread,
    openSendNote: (member, opts = {}) => setSendTarget({ member, context: opts.context || null, onDone: opts.onDone || null, source: opts.source || null }),
    openThread: (id) => { setMessagesOpen(false); setThreadId(id); },
    openMessages: () => setMessagesOpen(true),
  };

  return (
    <NotesCtx.Provider value={value}>
      {children}
      {sendTarget && <SendNoteModal target={sendTarget} onClose={() => setSendTarget(null)} onSent={refreshUnread} />}
      {messagesOpen && <MessagesDrawer onClose={() => setMessagesOpen(false)} openThread={value.openThread} />}
      {threadId && <ThreadDrawer conversationId={threadId} onClose={() => setThreadId(null)} onChanged={afterThreadActivity} />}
    </NotesCtx.Provider>
  );
}

// ---- Header ✉ button -----------------------------------------------------------------------
export function HeaderMessagesButton() {
  const { unreadCount, openMessages } = useNotes();
  return (
    <button onClick={openMessages} data-testid="notes-header-messages-btn" title="Messages"
      className="relative flex items-center justify-center h-9 w-9 rounded-full hover:bg-white/10 transition-colors">
      <Mail className="h-[18px] w-[18px] text-white" />
      {unreadCount > 0 && (
        <span data-testid="notes-header-unread-badge"
          className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 inline-flex items-center justify-center rounded-full bg-brand text-ink text-[9px] font-extrabold">
          {unreadCount > 9 ? "9+" : unreadCount}
        </span>
      )}
    </button>
  );
}

// ---- Shared Send-Note modal ----------------------------------------------------------------
function SendNoteModal({ target, onClose, onSent }) {
  const m = target.member;
  const [text, setText] = useState("");
  const [phase, setPhase] = useState("compose"); // compose | sent | limit
  const [sending, setSending] = useState(false);
  const trimmed = text.trim();

  const submit = async () => {
    if (!trimmed || sending) return;
    setSending(true);
    try {
      await api.post("/v1/build/notes/send", { recipient_id: m.id, text: trimmed, context: target.context || undefined, source: target.source || undefined });
      setPhase("sent");
      onSent?.();
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "daily_limit_reached") setPhase("limit");
      else if (code === "cannot_note_self") toast.error("You can't send a note to yourself.");
      else if (code === "empty_message") toast.error("Write a short message first.");
      else if (code === "recipient_not_available") toast.error("This member can't receive notes right now.");
      else toast.error("Couldn't send your note — please try again.");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center" data-testid="send-note-modal">
      <div className="absolute inset-0 bg-ink/50" onClick={onClose} data-testid="send-note-backdrop" />
      <div className="relative w-full sm:w-[560px] max-h-[92vh] overflow-y-auto bg-paper rounded-t-2xl sm:rounded-xl shadow-2xl">
        {/* mobile grab handle */}
        <div className="sm:hidden pt-2.5 flex justify-center"><div className="h-1 w-9 rounded-full bg-ink/15" /></div>

        {phase === "compose" && (
          <>
            <div className="px-6 sm:px-7 pt-5 pb-4 border-b border-hairline">
              <div className="flex items-start justify-between">
                <span className="text-[13px] sm:text-[15px] font-extrabold uppercase tracking-[0.06em] text-ink">Send a note to {firstName(m.name)}</span>
                <button onClick={onClose} data-testid="send-note-close-btn" className="text-faint hover:text-ink"><X className="h-4 w-4" /></button>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <Avatar member={m} size={40} />
                <div className="min-w-0">
                  <div className="text-sm font-bold text-ink truncate">{m.name}</div>
                  <div className="text-[11.5px] text-ink-soft mt-0.5 truncate">{subtitleOf(m)}</div>
                </div>
              </div>
            </div>
            <div className="px-6 sm:px-7 pt-5">
              <p className="text-[13px] leading-relaxed text-ink-soft">Introduce yourself, ask about their work, or explore a collaboration.</p>
              <textarea value={text} onChange={(e) => setText(e.target.value.slice(0, NOTE_MAX))} autoFocus
                data-testid="send-note-textarea" rows={5} placeholder="Write your note…"
                className="mt-4 w-full rounded-lg border border-ink/15 bg-[#FCFBF9] px-4 py-3.5 text-[13.5px] leading-relaxed text-ink placeholder:text-faint focus:outline-none focus:border-brand min-h-[150px] resize-none" />
              <div className="mt-2 flex items-center justify-between text-[10.5px] text-faint">
                <span>One note per person, then it goes quiet.</span>
                <span data-testid="send-note-char-count">{text.length} / {NOTE_MAX}</span>
              </div>
            </div>
            <div className="mt-4 px-6 sm:px-7 py-4 border-t border-hairline bg-[#FCFBF9] flex items-center justify-end gap-3">
              <button onClick={onClose} data-testid="send-note-cancel-btn" className="text-[12.5px] font-semibold text-ink-soft hover:text-ink">Cancel</button>
              <button onClick={submit} disabled={!trimmed || sending} data-testid="send-note-submit-btn"
                className="rounded-lg bg-brand px-6 py-3 text-[12.5px] font-bold tracking-[0.03em] text-ink hover:bg-brand-dim transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                {sending ? "Sending…" : "Send Note"}
              </button>
            </div>
          </>
        )}

        {phase === "sent" && (
          <div className="px-7 py-8 text-center" data-testid="send-note-sent-state">
            <div className="mx-auto h-10 w-10 rounded-full border-[1.5px] border-brand flex items-center justify-center text-[#8A6A00] text-base">✓</div>
            <div className="mt-4 text-[15px] font-bold text-ink">Note sent to {firstName(m.name)}</div>
            <div className="mt-1.5 text-[12.5px] leading-relaxed text-ink-soft">You'll get a notification if they reply.</div>
            <div className="mt-5 flex justify-center gap-2.5">
              <button onClick={() => { onClose(); target.onDone?.(); }} data-testid="send-note-done-btn" className="rounded-lg bg-ink px-4 py-2.5 text-[11.5px] font-bold text-paper hover:bg-ink/90 transition-colors">{target.context ? "Done" : "Back to Discover"}</button>
            </div>
          </div>
        )}

        {phase === "limit" && (
          <div data-testid="send-note-limit-state">
            <div className="px-7 pt-6">
              <div className="text-[9.5px] font-extrabold uppercase tracking-[0.15em] text-faint">Daily limit reached</div>
              <div className="mt-3 flex items-center gap-3">
                <Avatar member={m} size={34} />
                <div className="min-w-0"><div className="text-[13px] font-bold text-ink truncate">{m.name}</div><div className="text-[11px] text-ink-soft truncate">{subtitleOf(m)}</div></div>
              </div>
              <div className="mt-3 rounded-r-lg border-l-[3px] border-brand bg-[#FFF7DC] px-3.5 py-3 text-[11.5px] leading-relaxed text-[#5C4A14]">
                You've reached today's note limit — try again tomorrow.
              </div>
            </div>
            <div className="mt-4 px-7 py-4 border-t border-hairline bg-[#FCFBF9] flex items-center justify-end">
              <button onClick={onClose} data-testid="send-note-limit-close-btn" className="text-[12px] font-semibold text-ink-soft hover:text-ink">Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Header MESSAGES drawer ----------------------------------------------------------------
function MessagesDrawer({ onClose, openThread }) {
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => { api.get("/v1/build/notes/conversations").then((r) => setData(r.data)).catch(() => setData({ items: [], unread_count: 0 })); }, []);
  const items = data?.items || [];
  const shown = showAll ? items : items.slice(0, 5);
  const unread = data?.unread_count || 0;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="messages-drawer">
      <div className="absolute inset-0 bg-ink/30" onClick={onClose} data-testid="messages-drawer-backdrop" />
      <div className="relative mt-16 sm:mt-[76px] mr-0 sm:mr-6 h-fit max-h-[80vh] w-full sm:w-[360px] overflow-hidden bg-paper border border-ink/10 rounded-none sm:rounded-xl shadow-2xl">
        <div className="flex items-center justify-between px-[18px] py-[15px] border-b border-hairline">
          <div className="flex items-center gap-2">
            <span className="text-[9.5px] font-extrabold uppercase tracking-[0.16em] text-ink">Messages</span>
            {unread > 0 && <span className="text-[10.5px] text-faint">{unread} unread</span>}
          </div>
          <button onClick={onClose} data-testid="messages-drawer-close-btn" className="text-faint hover:text-ink"><X className="h-4 w-4" /></button>
        </div>
        {data === null ? (
          <div className="px-[18px] py-8 text-center text-xs text-faint">Loading…</div>
        ) : items.length === 0 ? (
          <div className="px-6 py-10 text-center" data-testid="messages-drawer-empty">
            <Mail className="mx-auto h-5 w-5 text-ink/20" />
            <div className="mt-3 text-[12.5px] font-semibold text-ink-soft">No messages yet</div>
            <div className="mt-1 text-[11px] text-faint">Say hello to someone in Discover.</div>
          </div>
        ) : (
          <>
            <div className="divide-y divide-hairline max-h-[60vh] overflow-y-auto" data-testid="messages-drawer-list">
              {shown.map((c) => {
                const other = c.other_participant || {};
                return (
                  <button key={c.id} onClick={() => openThread(c.id)} data-testid={`messages-drawer-row-${c.id}`}
                    className={`w-full flex items-center gap-[11px] px-[18px] py-3.5 text-left hover:bg-surface2/40 transition-colors ${c.unread ? "bg-[#FFFDF4]" : ""}`}>
                    <Avatar member={other} size={34} />
                    <div className="flex-1 min-w-0">
                      <div className={`text-[12.5px] truncate ${c.unread ? "font-bold text-ink" : "font-semibold text-[#3C3A36]"}`}>{other.name || "Member"}</div>
                      <div className={`mt-0.5 text-[11px] truncate ${c.unread ? "text-ink-soft" : "text-faint"}`}>{c.last_message_preview}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <span className="text-[10px] text-faint">{relTime(c.last_message_at)}</span>
                      {c.unread && <span className="h-1.5 w-1.5 rounded-full bg-brand" />}
                    </div>
                  </button>
                );
              })}
            </div>
            {items.length > 5 && (
              <button onClick={() => setShowAll((v) => !v)} data-testid="messages-drawer-view-all"
                className="w-full px-[18px] py-3 border-t border-hairline bg-[#FCFBF9] text-left text-[11px] font-bold tracking-[0.05em] text-[#8A6A00] hover:text-brand-dim">
                {showAll ? "Show recent ←" : "VIEW ALL →"}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---- Thread drawer -------------------------------------------------------------------------
function ThreadDrawer({ conversationId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportBusy, setReportBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/v1/build/notes/conversations/${conversationId}`);
      setData(r.data);
      await api.post(`/v1/build/notes/${conversationId}/mark-read`).catch(() => {});
      onChanged?.();
    } catch { toast.error("Couldn't open this conversation."); onClose(); }
  }, [conversationId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const other = data?.conversation?.other_participant || {};
  const messages = data?.messages || [];
  const nameById = (id) => (id === other.id ? other.name : "You");

  const sendReply = async () => {
    const t = reply.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      await api.post(`/v1/build/notes/${conversationId}/reply`, { text: t.slice(0, NOTE_MAX) });
      setReply("");
      await load();
    } catch { toast.error("Couldn't send your reply."); }
    finally { setSending(false); }
  };

  const report = async () => {
    setReportBusy(true);
    try {
      await api.post(`/v1/build/notes/${conversationId}/report`, { reason: reportReason.trim().slice(0, 500) });
      toast.success("Reported to NJM. Thank you.");
      setReporting(false); setReportReason("");
    } catch { toast.error("Couldn't submit the report."); }
    finally { setReportBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" data-testid="thread-drawer">
      <div className="absolute inset-0 bg-ink/50" onClick={onClose} data-testid="thread-drawer-backdrop" />
      <div className="relative w-full sm:w-[440px] h-full bg-paper flex flex-col shadow-2xl">
        <div className="bg-ink px-6 py-5 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <Avatar member={other} size={40} />
            <div className="min-w-0">
              <div className="text-[15px] font-bold text-paper truncate">{other.name || "Member"}</div>
              <div className="text-[11px] text-white/60 mt-0.5 truncate">{subtitleOf(other)}</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="thread-drawer-close-btn" className="text-white/60 hover:text-white shrink-0"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5" data-testid="thread-drawer-messages">
          {data === null ? (
            <div className="text-xs text-faint">Loading…</div>
          ) : messages.length === 0 ? (
            <div className="text-xs text-faint">No messages yet.</div>
          ) : (
            messages.map((msg, i) => (
              <div key={msg.id}>
                {i > 0 && <div className="my-5 h-px bg-hairline" />}
                <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-faint">{stampOf(msg, nameById(msg.sender_id))}</div>
                <div className="mt-2 text-[13px] leading-relaxed text-ink whitespace-pre-wrap">{msg.text}</div>
              </div>
            ))
          )}
        </div>

        <div className="px-6 py-4 border-t border-hairline bg-[#FCFBF9]">
          {reporting ? (
            <div data-testid="thread-drawer-report-panel">
              <div className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-ink">Report to NJM</div>
              <div className="mt-1 text-[11px] leading-relaxed text-ink-soft">A moderator will review this conversation. Add an optional note.</div>
              <textarea value={reportReason} onChange={(e) => setReportReason(e.target.value.slice(0, 500))} rows={2} autoFocus
                data-testid="thread-drawer-report-reason" placeholder="What's wrong? (optional)"
                className="mt-3 w-full rounded-lg border border-ink/15 bg-paper px-3.5 py-3 text-[12.5px] leading-relaxed text-ink placeholder:text-faint focus:outline-none focus:border-brand resize-none" />
              <div className="mt-3 flex items-center justify-end gap-3">
                <button onClick={() => { setReporting(false); setReportReason(""); }} data-testid="thread-drawer-report-cancel" className="text-[11.5px] font-semibold text-ink-soft hover:text-ink">Cancel</button>
                <button onClick={report} disabled={reportBusy} data-testid="thread-drawer-report-submit"
                  className="rounded-lg bg-ink px-5 py-2.5 text-[11.5px] font-bold text-paper hover:bg-ink/90 transition-colors disabled:opacity-40">
                  {reportBusy ? "Reporting…" : "Report"}
                </button>
              </div>
            </div>
          ) : (
            <>
              <textarea value={reply} onChange={(e) => setReply(e.target.value.slice(0, NOTE_MAX))} rows={2}
                data-testid="thread-drawer-reply-input" placeholder="Write a reply…"
                className="w-full rounded-lg border border-ink/15 bg-paper px-3.5 py-3 text-[12.5px] leading-relaxed text-ink placeholder:text-faint focus:outline-none focus:border-brand resize-none" />
              <div className="mt-3 flex items-center justify-between">
                <button onClick={() => setReporting(true)} data-testid="thread-drawer-report-btn" className="text-[11px] text-faint hover:text-ink transition-colors">Report</button>
                <button onClick={sendReply} disabled={!reply.trim() || sending} data-testid="thread-drawer-send-btn"
                  className="rounded-lg bg-brand px-6 py-3 text-[12px] font-bold text-ink hover:bg-brand-dim transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                  {sending ? "Sending…" : "Send"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
